<template>
  <v-dialog
    eager
    scrollable
    content-class="custom-confirm-dialog"
    :max-width="width"
    :model-value="shouldShow"
    :persistent="persistent"
    :style="'border: 1px solid red'"
    @keydown.esc="choose(false)"
    @keydown.enter.prevent="choose(true)"
    @update:model-value="change">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between px-5 py-4">
        <h2
          class="typography-heading-md-bold ma-0 flex-1 d-flex flex-row align-center justify-start ga-2">
          <template v-if="deleteConfirmation">
            <v-icon size="20" color="red">{{ icon }}</v-icon>
            <span class="typography-heading-md-bold">{{ title }}</span>
            <span class="typography-heading-md-bold" v-if="entityName">
              {{ entityName }}
            </span>
          </template>

          <template v-else>
            <v-icon size="20" v-if="Boolean(icon)">{{ icon }}</v-icon>
            <span class="typography-heading-md-bold">{{ title }}</span>

            <v-btn
              v-if="allowClose"
              class="ml-auto"
              density="default"
              icon="mdi-close"
              size="small"
              variant="text"
              :aria-label="$t('global.basicTerms.close')"
              @click="$emit('close')"></v-btn>
          </template>
        </h2>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="px-6 py-4 typography-body-md">
        <div class="d-flex flex-column ga-5">
          <slot v-if="translatedMessage" name="message"></slot>
          <span
            v-if="!$slots.message && translatedMessage"
            class="typography-body-md"
            v-html="translatedMessage"></span>

          <v-alert
            v-if="deleteConfirmation && showDeleteWarning"
            variant="tonal"
            prominent
            border="start"
            color="error">
            <span class="d-flex flex-row ga-1 align-baseline">
              <v-icon color="error" icon="mdi-alert" size="16" />
              <span class="d-flex flex-column ga-1">
                <span class="typography-body-md-bold">{{ $t('global.basicTerms.warning') }}!</span>
                <span class="capitalize-first-letter typography-body-sm">
                  {{ $t('global.phrases.thisActionCannotBeUndone') }}.
                </span>
              </span>
            </span>
          </v-alert>

          <div v-if="confirmationInputText" class="d-flex flex-column ga-1">
            <span
              class="typography-body-md"
              v-html="
                $t('lists.common.messages.typeToConfirm', {
                  message: confirmationInputText
                })
              "></span>

            <v-text-field
              data-testid="confirm-text-field"
              density="comfortable"
              hide-details="auto"
              color="primary"
              variant="outlined"
              v-model="confirmationTextValue"
              :rules="[rules.confirmTextMatch]"
              @paste="handleConfirmPaste"
              />
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="d-flex justify-end ga-2 px-5 py-4">
        <slot name="custom-actions" :choose="choose">
          <v-spacer />
          <slot name="false-action" :choose="choose">
            <v-btn
              v-if="Boolean(translatedButtonFalseText)"
              :color="buttonFalseColor"
              :variant="buttonFalseFlat ? 'outlined' : undefined"
              :disabled="loading"
              @click="choose(false)">
              {{ translatedButtonFalseText }}
            </v-btn>
          </slot>

          <slot name="true-action" :choose="choose">
            <v-btn
              v-if="Boolean(translatedButtonTrueText)"
              :color="buttonTrueColor"
              :variant="buttonTrueFlat ? 'flat' : undefined"
              :loading="loading"
              :disabled="!isConfirmTextMatched"
              @click="choose(true)">
              {{ translatedButtonTrueText }}
            </v-btn>
          </slot>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { $t } from '@satellite/plugins/translator';

export default {
  props: {
    buttonTrueText: {
      type: String
    },
    buttonFalseText: {
      type: String
    },
    buttonTrueColor: {
      type: String,
      default: 'primary'
    },
    buttonFalseColor: {
      type: String,
      default: 'black'
    },
    buttonFalseFlat: {
      type: Boolean,
      default: true
    },
    buttonTrueFlat: {
      type: Boolean,
      default: true
    },
    color: {
      type: String,
      default: 'warning'
    },
    icon: {
      type: String,
      default: 'mdi-help-circle-outline'
    },
    message: {
      type: String,
      required: false
    },
    persistent: Boolean,
    title: {
      type: String,
      default: 'Confirm'
    },
    width: {
      type: Number,
      default: 600
    },
    confirmationInputText: {
      type: String,
      default: null
    },
    preventConfirmPaste: {
      type: Boolean,
      required: false,
      default: true
    },
    pasteRejectionMsg: {
      type: String,
      required: false
    },
    shouldShow: {
      type: Boolean,
      required: false,
      default: true
    },
    isManualMode: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    deleteConfirmation: {
      type: Boolean,
      required: false,
      default: false
    },
    showDeleteWarning: {
      type: Boolean,
      required: false,
      default: true
    },
    entityName: {
      type: String,
      required: false,
      default: null
    },
    darkTheme: {
      type: Boolean,
      required: false,
      default: true
    },
    allowClose: {
      type: Boolean,
      required: false,
      default: false
    },
    // This is weird, but this.notify is not available when confirm is called in script of component
    // So we pass it instead
    pasteNotifyFn: {
      type: Function,
      required: false
    }
  },
  setup() {},
  data() {
    return {
      value: false,
      confirmationTextValue: '',
      rules: {
        confirmTextMatch: () =>
          this.isConfirmTextMatched || `Please confirm by typing "${this.confirmationInputText}"`
      }
    };
  },
  computed: {
    translatedButtonTrueText() {
      const deleteConfirmation = this.buttonTrueText ?? $t('global.buttons.yesDelete');
      const otherConfirmation = this.buttonTrueText ?? $t('global.buttons.confirm');
      return this.deleteConfirmation ? deleteConfirmation : otherConfirmation;
    },
    translatedButtonFalseText() {
      return this.buttonFalseText ?? $t('global.buttons.nevermind');
    },
    isConfirmTextMatched() {
      return (
        !this.confirmationInputText ||
        this.confirmationInputText.toLowerCase() === this.confirmationTextValue.toLowerCase()
      );
    },
    translatedMessage() {
      return this.message ?? $t('global.phrases.areYouSure');
    },
    translatedPasteRejectionMessage() {
      return (
        this.pasteRejectionMsg ?? $t('global.messages.theConfirmationFieldDoesNotSupportPasting')
      );
    }
  },
  methods: {
    $t,
    choose(value) {
      if (value && !this.isConfirmTextMatched) {
        return;
      }
      this.$emit('result', value);
      this.value = value;

      this.destroyComponent();
    },
    change() {
      this.destroyComponent();
    },
    destroyComponent() {
      if (!this.isManualMode) {
        if (this.$el?.parentNode) {
          this.$el.parentNode.removeChild(this.$el);
        }
      }
    },
    handleConfirmPaste(e) {
      if (this.preventConfirmPaste) {
        e.preventDefault();
        if (this.pasteNotifyFn) {
          this.pasteNotifyFn(this.translatedPasteRejectionMessage, 'error');
        } else {
          this.notify(this.translatedPasteRejectionMessage, 'error');
        }
      }
    }
  }
};
</script>
